import { create } from 'zustand';
import type { BaseSource } from './MapController.js';

/**
 * Base chart + availability/attribution for the seamark overlay. The app shows
 * one chart — the ENC ("Elektronisk sjøkart"). There is no user-facing chart
 * picker; Kartverket is the default until the server confirms ENC works, so a
 * deployment without (or with a broken) ENC endpoint never shows a blank map.
 */
interface LayerInfo {
  available: boolean;
  attribution: string;
}

const SOURCE_KEY = 'los.baseSource';

/** Last source the server confirmed, so offline starts keep showing downloaded
 *  ENC regions; Kartverket until the server has answered at least once. */
function loadSource(): BaseSource {
  try {
    if (localStorage.getItem(SOURCE_KEY) === 'enc') return 'enc';
  } catch {
    /* storage unavailable */
  }
  return 'kartverket';
}

function saveSource(source: BaseSource): void {
  try {
    localStorage.setItem(SOURCE_KEY, source);
  } catch {
    /* storage unavailable */
  }
}

interface SourcesResponse {
  skippo: LayerInfo;
  enc: LayerInfo;
  seamark: LayerInfo;
}

interface BaseLayerState {
  source: BaseSource;
  skippo: LayerInfo;
  enc: LayerInfo;
  seamark: LayerInfo;
  setSource: (s: BaseSource) => void;
  loadSources: () => Promise<void>;
}

const DEFAULT_INFO: LayerInfo = { available: false, attribution: '' };

export const useBaseLayerStore = create<BaseLayerState>((set, get) => ({
  source: loadSource(),
  skippo: DEFAULT_INFO,
  enc: DEFAULT_INFO,
  seamark: DEFAULT_INFO,

  setSource: (source) => set({ source }),

  loadSources: async () => {
    let res: Response;
    try {
      res = await fetch('/base/sources');
    } catch {
      return; // offline — keep the preferred source so downloaded regions still show
    }
    let data: SourcesResponse | null = null;
    try {
      if (res.ok) data = (await res.json()) as SourcesResponse;
    } catch {
      /* not JSON, e.g. the SPA fallback page */
    }
    if (data) set({ skippo: data.skippo, enc: data.enc, seamark: data.seamark });
    // Online: the server's answer decides. Prefer ENC when offered; an unusable
    // answer counts as "not offered".
    const source: BaseSource = data?.enc.available ? 'enc' : 'kartverket';
    saveSource(source);
    if (get().source !== source) get().setSource(source);
  },
}));

/** Attribution for the currently selected base source. */
export function baseAttribution(state: BaseLayerState): string {
  if (state.source === 'skippo') return state.skippo.attribution;
  if (state.source === 'enc') return state.enc.attribution;
  return '';
}

/** Tile-proxy path for the active base source — offline downloads cache from here
 *  so the saved region matches the chart shown on screen. */
export function baseTilePath(source: BaseSource): string {
  if (source === 'enc') return '/base/enc';
  if (source === 'skippo') return '/base/skippo';
  return '/tiles';
}
